import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Paper,
  Tabs,
  Tab,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Trans, t } from '@lingui/macro'
import DateRangePickerInput from '@/components/ui/inputs/DateRangePickerInput'
import { DurationDistributionComponent } from './charts/DurationDistributionComponent'
import MedianDurationTrendsComponent from './charts/MedianDurationTrendsComponent'
import { ChartSkeleton } from '@/features/statistics'

type DurationSectionProps = {
  isExpanded: boolean
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void
  selectedRange: [Date, Date]
  onRangeChange: (range: { start: Date | null; end: Date | null }) => void
  durationMetrics: {
    showByType: boolean
    handleShowByTypeChange: (checked: boolean) => void
    selectedConsultationType: string | null
    handleConsultationTypeChange: (event: React.SyntheticEvent, value: string | null) => void
    consultationTypes: string[]
    getTypeDisplayName: (type: string) => string
    durationMetrics: any
    isLoadingDuration: boolean
  }
  isMobile: boolean
}

export function DurationSection({
  isExpanded,
  onChange,
  selectedRange,
  onRangeChange,
  durationMetrics,
  isMobile,
}: DurationSectionProps) {
  return (
    <Accordion
      expanded={isExpanded}
      onChange={onChange}
      TransitionProps={{ unmountOnExit: false }}
      sx={{
        '& .MuiAccordionSummary-content': {
          margin: isMobile ? '10px 0' : undefined,
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="duration-content" id="duration-header">
        <Typography variant="h6" sx={{ fontSize: isMobile ? '1rem' : undefined }}>
          <Trans>Duration</Trans>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12}>
            <DateRangePickerInput
              value={selectedRange}
              onChange={onRangeChange}
              label={t`Select Date Range`}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  fontSize: isMobile ? '0.875rem' : undefined,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={durationMetrics.showByType}
                  onChange={(e) => durationMetrics.handleShowByTypeChange(e.target.checked)}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={t`Split by Type`}
              sx={{
                mb: 2,
                pl: 1,
                '& .MuiFormControlLabel-label': {
                  fontWeight: 'medium',
                  fontSize: isMobile ? '0.875rem' : '0.95rem',
                },
              }}
            />

            {durationMetrics.showByType && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  mb: 3,
                  p: isMobile ? 0.5 : 1,
                  borderRadius: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <Typography
                  variant={isMobile ? 'body1' : 'subtitle1'}
                  sx={{
                    mb: 1,
                    px: 1,
                    fontSize: isMobile ? '0.875rem' : undefined,
                  }}
                >
                  <Trans>Select Consultation Type</Trans>:
                </Typography>
                <Tabs
                  value={durationMetrics.selectedConsultationType}
                  onChange={durationMetrics.handleConsultationTypeChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      minWidth: 'auto',
                      px: isMobile ? 1 : 2,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    },
                  }}
                >
                  <Tab
                    label={t`All Types`}
                    value={null}
                    sx={{
                      fontWeight: durationMetrics.selectedConsultationType === null ? 'bold' : 'normal',
                    }}
                  />
                  {durationMetrics.consultationTypes.map((type) => (
                    <Tab
                      key={type}
                      label={durationMetrics.getTypeDisplayName(type)}
                      value={type}
                      sx={{
                        '&.Mui-selected': {
                          fontWeight: 'bold',
                        },
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    />
                  ))}
                </Tabs>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12}>
            {durationMetrics.isLoadingDuration ? (
              <ChartSkeleton />
            ) : (
              <DurationDistributionComponent
                data={durationMetrics.durationMetrics}
                title={t`Duration Distribution`}
                includeByType={durationMetrics.showByType}
                selectedType={durationMetrics.selectedConsultationType}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {durationMetrics.isLoadingDuration ? (
              <ChartSkeleton />
            ) : (
              <MedianDurationTrendsComponent
                data={durationMetrics.durationMetrics}
                title={t`Duration Trends`}
                includeByType={durationMetrics.showByType}
                selectedType={durationMetrics.selectedConsultationType}
              />
            )}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}
